
/**
 * Module dependencies.
 */

import { ChangeEvent, ForwardedRef, ReactNode, forwardRef } from 'react';
import { FieldAlert } from './forms/field-alert';
import { FormGroup, textDesktopStyle } from './forms/fields-styled-components';
import { Icon } from 'react-components/media';
import { color } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import checkedIcon from './assets/svg/forms/checkbox-checked.svg';
import styled, { css } from 'styled-components';
import uncheckedIcon from './assets/svg/forms/checkbox.svg';

/**
 * Export `CheckboxProps` type.
 */

export type CheckboxProps = {
  'aria-describedby'?: string;
  'aria-label'?: string;
  checked?: boolean;
  className?: string;
  disabled?: boolean;
  hasError?: boolean;
  helpText?: string;
  isOpen?: boolean;
  label?: ReactNode;
  name: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
};

/**
 * Checkbox size.
 */

const checkboxSize = '24px';

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon).attrs({ 'aria-hidden': true })`
  grid-area: checkbox;
  max-width: ${checkboxSize};
  transition: ${theme('animations.defaultTransition')};
  transition-property: color, opacity;
  width: ${checkboxSize};
`;

/**
 * Export `CheckedIcon` styled component.
 */

export const CheckedIcon = styled(StyledIcon)`
  color: ${color('primary')};
  opacity: 0;
`;

/**
 * Export `UncheckedIcon` styled component.
 */

export const UncheckedIcon = styled(StyledIcon)`
  color: ${color('gray500')};
  opacity: 1;
`;

/**
 * `Label` styled component.
 */

const Label = styled.label<{ disabled: boolean; hasError: boolean }>`
  ${textDesktopStyle}

  align-items: center;
  color: ${color('secondary')};
  column-gap: 4px;
  cursor: pointer;
  display: grid;
  grid-template-areas: 'checkbox label';
  grid-template-columns: ${checkboxSize} 1fr;
  transition: color ${theme('animations.defaultTransition')};

  ${ifProp('hasError', css`
    color: ${color('danger')};
  `)}

  ${ifProp('disabled', css`
    color: ${color('gray400')};
  `)}
`;

/**
 * Export `LabelText` styled component.
 */

export const LabelText = styled.span`
  grid-area: label;
`;

/**
 * `Input` styled component.
 */

const Input = styled.input`
  cursor: pointer;
  grid-area: checkbox;
  height: ${checkboxSize};
  opacity: 0;
  width: ${checkboxSize};
  z-index: 1;

  &:checked {
    & ~ ${UncheckedIcon} {
      opacity: 0;
    }

    & ~ ${CheckedIcon} {
      opacity: 1;
    }
  }

  &:focus-visible {
    & ~ ${UncheckedIcon} {
      color: ${color('blue200')};
    }

    &:checked ~ ${CheckedIcon} {
      .checkbox-icon-stroke-color {
        stroke: ${color('blue200')};
      }
    }
  }

  &:hover {
    & ~ ${UncheckedIcon} {
      color: ${color('gray600')};
    }

    &:checked ~ ${CheckedIcon} {
      color: ${color('blue600')};
    }
  }

  /* stylelint-disable selector-type-no-unknown */
  &:disabled {
    & ~ ${UncheckedIcon},
    &:checked ~ ${CheckedIcon} {
      color: ${color('gray400')};
    }
  }
  /* stylelint-enable selector-type-no-unknown */
`;

/**
 * `Checkbox` component.
 */

function Checkbox(props: CheckboxProps, ref: ForwardedRef<HTMLInputElement>): JSX.Element {
  const { className, disabled = false, hasError = false, helpText, isOpen, label, ...rest } = props;

  return (
    <FormGroup
      className={className}
      disabled={disabled}
    >
      <Label
        disabled={disabled}
        hasError={hasError}
      >
        {label && (
          <LabelText>
            {label}
          </LabelText>
        )}

        <Input
          disabled={disabled}
          ref={ref}
          type={'checkbox'}
          {...rest}
        />

        <UncheckedIcon icon={uncheckedIcon} />

        <CheckedIcon icon={checkedIcon} />
      </Label>

      {helpText && (
        <FieldAlert isOpen={isOpen ?? !hasError}>
          {helpText}
        </FieldAlert>
      )}
    </FormGroup>
  );
}

/**
 * Forwarded `Checkbox`.
 */

const ForwardedCheckbox = forwardRef<HTMLInputElement, CheckboxProps>(Checkbox);

/**
 * Export `BaseCheckbox` component.
 */

export { ForwardedCheckbox as BaseCheckbox };
